/**
 * navigateLinks — координаты поля + deep-link'и маршрута в навигатор.
 *
 * AK-372 (founder approve 2026-07-06): с мобильного браузера кликнул поле →
 * координаты → одним тапом маршрут в Яндекс/Google/2ГИС. V1 точка = центроид
 * поля (навигатор доводит до ближайшего съезда — норм, как у всех агро-приложений).
 *
 * Три провайдера, у каждого app-схема (открывает установленное приложение) +
 * web-фолбэк (браузерная карта, если приложения нет). ⚠ 2ГИС ждёт lng,lat
 * (обратный порядок), Яндекс/Google — lat,lng. Ошибка порядка = маршрут
 * улетает в другое полушарие.
 *
 * Открытие ссылки (openNavigator) на мобайле пытается app-схему и через ~1.5с
 * без ухода со страницы падает на web-фолбэк; на десктопе сразу открывает web.
 */

export interface NavigatorLink {
  /** Deep-link, открывающий установленное приложение навигатора. */
  app: string;
  /** Web-фолбэк — браузерная карта провайдера. */
  web: string;
}

export interface NavigatorLinks {
  yandex: NavigatorLink;
  google: NavigatorLink;
  dgis: NavigatorLink;
  apple: NavigatorLink;
}

/** Идентификатор провайдера карт для in-app меню выбора (AK-380). */
export type NavigatorProviderKey = 'yandex' | 'dgis' | 'google' | 'apple';

/** Провайдер карт: ключ, человекочитаемое имя и app+web ссылки. */
export interface NavigatorProvider {
  key: NavigatorProviderKey;
  /** Имя для пункта меню («Яндекс Карты» / «2ГИС» / «Google» / «Apple»). */
  label: string;
  link: NavigatorLink;
}

/** Координаты «lat, lng» с фиксированными 5 знаками после запятой. */
export function formatCoords(lat: number, lng: number): string {
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

/**
 * Собирает app + web ссылки для четырёх карт-провайдеров на точку (lat, lng).
 *
 * ⚠ AK-380: Яндекс = ЯНДЕКС КАРТЫ, не Навигатор. Раньше app-ссылка вела в
 * `yandexnavi://` (Навигатор). Теперь используем universal-link Яндекс Карт
 * `https://yandex.ru/maps/?rtext=~lat,lng&rtt=auto` — на iOS/Android он
 * открывает установленное приложение Яндекс Карты (associated-domain у Яндекса),
 * а без приложения падает в браузерную карту. Это надёжный документированный
 * путь Яндекс Карт (в отличие от сырой app-схемы `yandexmaps://`, чей формат
 * маршрута нестабилен между версиями). app === web у Яндекса и Google.
 *
 * Google — единый universal-link (app === web).
 * 2ГИС — app-схема `dgis://` + web, ⚠ порядок координат lng,lat (обратный).
 * Apple Maps — `maps://?daddr=` (app) + `https://maps.apple.com/?daddr=` (web).
 */
export function navigatorLinks(lat: number, lng: number): NavigatorLinks {
  const googleUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  // Яндекс Карты universal-link: открывает приложение Яндекс Карты, иначе — web.
  const yandexUrl = `https://yandex.ru/maps/?rtext=~${lat},${lng}&rtt=auto`;
  return {
    yandex: {
      app: yandexUrl,
      web: yandexUrl,
    },
    google: {
      app: googleUrl,
      web: googleUrl,
    },
    dgis: {
      // ⚠ 2ГИС принимает координаты в порядке lng,lat.
      app: `dgis://2gis.ru/routeSearch/rsType/car/to/${lng},${lat}`,
      web: `https://2gis.ru/directions/points/|${lng},${lat}`,
    },
    apple: {
      app: `maps://?daddr=${lat},${lng}`,
      web: `https://maps.apple.com/?daddr=${lat},${lng}`,
    },
  };
}

/**
 * Упорядоченный список провайдеров для in-app меню выбора навигатора (AK-380).
 *
 * Порядок: Яндекс Карты · 2ГИС · Google · Apple — по популярности у аграриев РФ.
 * Используется на iOS-ветке кнопки «Маршрут» (in-app меню вместо share-sheet)
 * и в web-меню «Открыть в…».
 */
export function navigatorProviders(lat: number, lng: number): NavigatorProvider[] {
  const links = navigatorLinks(lat, lng);
  return [
    { key: 'yandex', label: 'Яндекс Карты', link: links.yandex },
    { key: 'dgis', label: '2ГИС', link: links.dgis },
    { key: 'google', label: 'Google', link: links.google },
    { key: 'apple', label: 'Apple', link: links.apple },
  ];
}

/** Грубая проверка мобильного браузера — для выбора app-схемы vs web. */
export function isMobileBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|Mobile/i.test(
    navigator.userAgent,
  );
}

/** Платформа для выбора механики «одной кнопки Маршрут» (AK-373 п.7). */
export type RoutePlatform = 'android' | 'ios' | 'desktop';

/**
 * Определить платформу для навигатора-одной-кнопки.
 *
 *   android → `geo:`-URI → системный chooser ОС со всеми карто-приложениями.
 *   ios     → share sheet (navigator.share) — в iOS нет chooser'а как класса,
 *             share ближайший аналог «выбери, чем открыть».
 *   desktop → web-версия карты в новой вкладке.
 *
 * iPadOS 13+ маскируется под Mac; отличаем тач-планшет по maxTouchPoints.
 */
export function detectPlatform(): RoutePlatform {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent;
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  // iPadOS 13+ шлёт Mac-UA — ловим по тач-точкам (десктопный Mac = 0).
  if (/Macintosh/i.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1) {
    return 'ios';
  }
  return 'desktop';
}

/**
 * Механика первичной кнопки «Маршрут»: как отдаём выбор навигатора.
 *
 *   geo     → Android geo:-URI → системный chooser ОС.
 *   chooser → iOS: показываем СВОЁ in-app меню провайдеров (AK-380). В iOS нет
 *             системного chooser'а как класса; раньше слали в share-sheet
 *             (`share`), но это плохой UX (лишний слой «поделиться»). Теперь —
 *             явное меню Яндекс Карты / 2ГИС / Google / Apple.
 *   web     → десктоп: web-карта в новой вкладке.
 */
export type PrimaryRouteKind = 'geo' | 'chooser' | 'web';

export interface PrimaryRouteLink {
  kind: PrimaryRouteKind;
  /**
   * URL для механик geo/web. Для `chooser` (iOS) единственного URL нет — UI
   * рендерит меню провайдеров через `navigatorProviders`; поле держит
   * Apple Maps universal-link как разумный дефолт/фолбэк.
   */
  url: string;
}

/**
 * Собрать первичную ссылку «Маршрут» под платформу (AK-373 п.7 / AK-380).
 *
 *   android → `geo:lat,lng?q=lat,lng` — Android показывает системный выбор
 *             приложения (Яндекс/Google/2ГИС/…); `q` даёт метку точки.
 *   ios     → `chooser`: UI показывает in-app меню провайдеров (AK-380);
 *             url = Apple Maps universal-link как фолбэк.
 *   desktop → Google Maps web-directions (открываем в новой вкладке).
 */
export function primaryRouteLink(lat: number, lng: number): PrimaryRouteLink {
  const platform = detectPlatform();
  if (platform === 'android') {
    return { kind: 'geo', url: `geo:${lat},${lng}?q=${lat},${lng}` };
  }
  if (platform === 'ios') {
    return { kind: 'chooser', url: `https://maps.apple.com/?daddr=${lat},${lng}` };
  }
  return {
    kind: 'web',
    url: `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
  };
}

/**
 * Открыть маршрут ОДНОЙ кнопкой (AK-373 п.7 / AK-380).
 *
 *   Android: `geo:`-URI через location.href → системный chooser карт.
 *   iOS:     kind === 'chooser' → сам по себе `openRoute` меню НЕ рисует (это
 *            UI-слой), поэтому здесь фолбэк — открыть первого провайдера
 *            (Яндекс Карты) через openNavigator. UI-компоненты (FieldCoordinates
 *            и попап /map) на iOS перехватывают кнопку и показывают in-app меню
 *            `navigatorProviders`, так что этот фолбэк срабатывает лишь если
 *            openRoute вызван напрямую вне UI-меню.
 *   Desktop: web-карта Google в новой вкладке.
 *
 * Не заменяет navigatorLinks/openNavigator — те остаются для явного выбора
 * конкретного навигатора. Это верхний слой «удобно по умолчанию».
 */
export function openRoute(lat: number, lng: number): void {
  if (typeof window === 'undefined') return;
  const link = primaryRouteLink(lat, lng);

  if (link.kind === 'geo') {
    window.location.href = link.url;
    return;
  }

  if (link.kind === 'chooser') {
    // iOS без UI-меню: открываем предпочтительного провайдера (Яндекс Карты).
    // Обычный путь — UI показывает меню navigatorProviders и вызывает
    // openNavigator на выбранном пункте; сюда попадаем только при прямом вызове.
    const providers = navigatorProviders(lat, lng);
    openNavigator(providers[0].link);
    return;
  }

  // desktop → web в новой вкладке.
  window.open(link.url, '_blank', 'noopener');
}

/**
 * Открыть маршрут в навигаторе.
 *
 * Десктоп → сразу web-версия в новой вкладке.
 * Мобайл → пробуем app-схему; если через ~1.5с страница осталась видимой
 * (приложение не перехватило переход) — открываем web-фолбэк. Слушаем
 * visibilitychange/blur, чтобы отменить фолбэк, когда приложение реально
 * поднялось (иначе web откроется поверх уже запущенного навигатора).
 */
export function openNavigator(link: NavigatorLink): void {
  if (typeof window === 'undefined') return;

  if (!isMobileBrowser()) {
    window.open(link.web, '_blank', 'noopener');
    return;
  }

  let switched = false;
  const onHidden = () => {
    if (document.visibilityState === 'hidden') switched = true;
  };
  const onBlur = () => {
    switched = true;
  };
  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('blur', onBlur);

  // Триггерим app-схему.
  window.location.href = link.app;

  window.setTimeout(() => {
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('blur', onBlur);
    // Приложение не перехватило переход (страница всё ещё видима) → web.
    if (!switched && document.visibilityState === 'visible') {
      window.open(link.web, '_blank', 'noopener');
    }
  }, 1500);
}
